// src/pages/admin/AdminLayout.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, Utensils, Users, LogOut,
  Search, Bell, ChevronDown, Map, MapPin, Truck, X, Zap,
  Menu as MenuIcon, Maximize, Minimize, ExternalLink, CalendarRange, Printer, Settings
} from 'lucide-react';
import { signOut } from '../../services/authService';
import { supabase } from '../../services/supabase';
import { updateOrderStatus } from '../../services/orderService';
import logoImg from '../../assets/logo-peaches.png';

// --- Auto-advance constants (same as AdminInstantOrders) ---
const AUTO_ACCEPT_SECONDS = 10;
const ADVANCE_MINUTES = 20;
const AUTO_READY_MINUTES = 20;
const AUTO_COMPLETE_MINUTES = 5;

const isScheduledOrder = (order) =>
  order.delivery_info?.fulfillment_mode === 'scheduled' && order.delivery_info?.scheduled_time;

const getMinutesUntilScheduled = (order, now) =>
  (new Date(order.delivery_info.scheduled_time) - now) / 60000;

const AdminLayoutStyles = () => (
  <style>{`
    :root {
      --saas-bg-root: #ffffff;
      --saas-bg-surface: #ffffff;
      --saas-sidebar-bg: #ffffff;
      --saas-sidebar-hover: rgba(47, 125, 94, 0.06);
      --saas-sidebar-active: #2F7D5E;
      --saas-sidebar-active-bg: rgba(47, 125, 94, 0.1);
      --saas-text-primary: #1e293b;
      --saas-text-secondary: #64748b;
      --saas-text-on-dark: #e2e8f0;
      --saas-border-color: #e2e8f0;
      --saas-primary: #2F7D5E;
      --saas-primary-hover: #245F48;
      --saas-primary-light: #d1fae5;
      --saas-radius: 8px;
      --saas-sidebar-gradient-start: #2F7D5E;
      --saas-sidebar-gradient-end: #48A67C;
      --saas-card-shadow: 0 1px 3px rgba(0,0,0,0.06);
      --saas-sidebar-width: 260px;
      --saas-sidebar-collapsed-width: 70px;
      --saas-header-height: 70px;
    }

    .admin-container {
      display: flex;
      height: 100vh;
      background: var(--saas-bg-root);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      overflow: hidden;
    }

    /* === Morvin-style White Sidebar === */
    .admin-sidebar {
      width: var(--saas-sidebar-width);
      background: var(--saas-sidebar-bg);
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      transition: width 0.3s ease;
      z-index: 50;
      border-right: 1px solid var(--saas-border-color);
      overflow: hidden;
    }

    .admin-sidebar[data-collapsed="true"] {
      width: var(--saas-sidebar-collapsed-width);
    }

    /* Gradient top section */
    .sidebar-profile-section {
      background: linear-gradient(135deg, var(--saas-sidebar-gradient-start), var(--saas-sidebar-gradient-end));
      padding: 24px 20px;
      display: flex;
      align-items: center;
      gap: 12px;
      min-height: 90px;
    }
    .sidebar-avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: rgba(255,255,255,0.25);
      border: 2px solid rgba(255,255,255,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      color: white;
      font-size: 1.1rem;
      flex-shrink: 0;
      overflow: hidden;
    }
    .sidebar-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .sidebar-user-info {
      overflow: hidden;
    }
    .admin-sidebar[data-collapsed="true"] .sidebar-user-info {
      display: none;
    }
    .sidebar-user-name {
      font-size: 0.95rem;
      font-weight: 600;
      color: white;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .sidebar-user-role {
      font-size: 0.8rem;
      color: rgba(255,255,255,0.8);
      white-space: nowrap;
    }

    /* Menu section label */
    .sidebar-section-label {
      padding: 20px 24px 8px;
      font-size: 0.7rem;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 1.2px;
    }
    .admin-sidebar[data-collapsed="true"] .sidebar-section-label {
      display: none;
    }

    .sidebar-menu {
      list-style: none;
      padding: 0 12px;
      margin: 0;
      flex: 1;
      overflow-y: auto;
    }

    .menu-item {
      padding: 11px 16px;
      display: flex;
      align-items: center;
      gap: 12px;
      color: var(--saas-text-secondary);
      cursor: pointer;
      font-weight: 500;
      font-size: 0.9rem;
      transition: all 0.2s;
      border-radius: 6px;
      margin-bottom: 2px;
      text-decoration: none;
      position: relative;
    }

    .menu-item:hover {
      background: var(--saas-sidebar-hover);
      color: var(--saas-primary);
    }

    .menu-item.active {
      background: var(--saas-sidebar-active-bg);
      color: var(--saas-primary);
      font-weight: 600;
    }

    .menu-item-badge {
      margin-left: auto;
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      border-radius: 10px;
      background: var(--saas-primary);
      color: white;
      font-size: 0.7rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
    .admin-sidebar[data-collapsed="true"] .menu-item-badge {
      position: absolute;
      top: 4px;
      right: 4px;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      font-size: 0.6rem;
    }

    .admin-sidebar[data-collapsed="true"] .menu-item span {
      display: none;
    }
    .admin-sidebar[data-collapsed="true"] .menu-item {
      justify-content: center;
      padding: 12px;
      border-left: none;
    }

    /* Sidebar footer */
    .sidebar-footer {
      padding: 16px;
      border-top: 1px solid var(--saas-border-color);
    }
    .logout-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px;
      border: 1px solid var(--saas-border-color);
      background: transparent;
      color: var(--saas-text-secondary);
      border-radius: var(--saas-radius);
      cursor: pointer;
      font-size: 0.85rem;
      font-weight: 500;
      transition: 0.2s;
    }
    .logout-btn:hover {
      background: #fef2f2;
      color: #dc2626;
      border-color: #fecaca;
    }
    .back-to-front-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px;
      border: 1px solid var(--saas-primary);
      background: transparent;
      color: var(--saas-primary);
      border-radius: var(--saas-radius);
      cursor: pointer;
      font-size: 0.85rem;
      font-weight: 500;
      transition: 0.2s;
      margin-bottom: 8px;
    }
    .back-to-front-btn:hover {
      background: var(--saas-primary);
      color: white;
    }
    .admin-sidebar[data-collapsed="true"] .back-to-front-btn span {
      display: none;
    }
    .admin-sidebar[data-collapsed="true"] .logout-btn span {
      display: none;
    }

    /* === Main content area === */
    .main-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      position: relative;
    }

    /* === Morvin-style Top Header === */
    .top-header {
      height: var(--saas-header-height);
      background: var(--saas-bg-surface);
      border-bottom: 1px solid var(--saas-border-color);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 24px;
      flex-shrink: 0;
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .hamburger-btn {
      width: 38px;
      height: 38px;
      border: 1px solid var(--saas-border-color);
      background: white;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--saas-text-secondary);
      transition: all 0.2s;
    }
    .hamburger-btn:hover {
      background: var(--saas-primary-light);
      color: var(--saas-primary);
    }
    .header-search {
      display: flex;
      align-items: center;
      gap: 10px;
      background: white;
      padding: 8px 16px;
      border-radius: 8px;
      width: 280px;
      color: var(--saas-text-secondary);
      border: 1px solid var(--saas-border-color);
      transition: border-color 0.2s;
    }
    .header-search:focus-within {
      border-color: var(--saas-primary);
      background: white;
    }
    .header-search input {
      border: none;
      background: transparent;
      outline: none;
      color: var(--saas-text-primary);
      width: 100%;
      font-size: 0.9rem;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .icon-btn {
      width: 38px;
      height: 38px;
      border: none;
      background: transparent;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--saas-text-secondary);
      transition: all 0.2s;
      position: relative;
    }
    .icon-btn:hover {
      background: var(--saas-bg-root);
      color: var(--saas-text-primary);
    }
    .notification-badge-count {
      position: absolute;
      top: 2px;
      right: 2px;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      background: #ef4444;
      color: white;
      border-radius: 9px;
      font-size: 0.65rem;
      font-weight: 700;
      line-height: 18px;
      text-align: center;
      animation: badgePop 0.3s ease;
    }
    @keyframes badgePop {
      0% { transform: scale(0); }
      60% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }

    .header-user-dropdown {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 6px 12px;
      border-radius: 8px;
      transition: background 0.2s;
      margin-left: 8px;
      position: relative;
    }
    .header-user-dropdown:hover {
      background: var(--saas-bg-root);
    }
    .header-user-avatar {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
    }
    .header-user-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .header-user-name {
      font-size: 0.9rem;
      font-weight: 500;
      color: var(--saas-text-primary);
    }
    .header-dropdown-menu {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 8px;
      background: white;
      border: 1px solid var(--saas-border-color);
      border-radius: 8px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.1);
      min-width: 180px;
      z-index: 100;
      padding: 8px;
      animation: dropdownIn 0.15s ease;
    }
    @keyframes dropdownIn {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .dropdown-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.9rem;
      color: var(--saas-text-primary);
      transition: background 0.2s;
      border: none;
      background: none;
      width: 100%;
    }
    .dropdown-item:hover {
      background: var(--saas-bg-root);
    }
    .dropdown-item-danger:hover {
      background: #fef2f2;
      color: #dc2626;
    }

    /* === Order Modal === */
    .order-modal-backdrop {
      position: fixed; inset: 0; z-index: 10000;
      background: rgba(0, 0, 0, 0.55);
      display: flex; align-items: center; justify-content: center;
      animation: backdropIn 0.3s ease;
    }
    @keyframes backdropIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .order-modal {
      background: white; border-radius: 16px; padding: 40px 36px 32px;
      width: 400px; max-width: 90vw;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      text-align: center;
      animation: modalBounceIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    @keyframes modalBounceIn {
      from { transform: scale(0.7) translateY(30px); opacity: 0; }
      to { transform: scale(1) translateY(0); opacity: 1; }
    }
    .modal-bell-wrapper {
      width: 72px; height: 72px; border-radius: 50%;
      background: linear-gradient(135deg, #2F7D5E, #48A67C);
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 20px;
      box-shadow: 0 8px 24px rgba(47, 125, 94, 0.35);
      animation: bellRing 1s ease 0.3s;
    }
    @keyframes bellRing {
      0%, 100% { transform: rotate(0); }
      10% { transform: rotate(20deg); }
      20% { transform: rotate(-20deg); }
      30% { transform: rotate(15deg); }
      40% { transform: rotate(-15deg); }
      50% { transform: rotate(10deg); }
      60% { transform: rotate(-10deg); }
      70% { transform: rotate(5deg); }
      80% { transform: rotate(-5deg); }
    }
    .modal-title {
      font-size: 1.5rem; font-weight: 800; color: #1e293b; margin-bottom: 8px;
    }
    .modal-order-number {
      font-family: monospace; font-size: 1.1rem; color: var(--saas-primary);
      font-weight: 700; margin-bottom: 6px;
    }
    .modal-amount {
      font-size: 2rem; font-weight: 800; color: #1e293b; margin: 16px 0;
    }
    .modal-customer {
      font-size: 0.95rem; color: #64748b; margin-bottom: 28px;
    }
    .modal-actions {
      display: flex; gap: 12px;
    }
    .modal-btn {
      flex: 1; padding: 14px; border-radius: 10px; font-size: 1rem;
      font-weight: 700; cursor: pointer; transition: all 0.2s; border: none;
    }
    .modal-btn-primary {
      background: var(--saas-primary); color: white;
    }
    .modal-btn-primary:hover { background: var(--saas-primary-hover); transform: translateY(-1px); }
    .modal-btn-secondary {
      background: white; color: #475569; border: 1px solid #e2e8f0;
    }
    .modal-btn-secondary:hover { background: var(--saas-primary-light); }

    .admin-content {
      flex: 1; padding: 24px; overflow-y: auto; scroll-behavior: smooth;
    }

    /* Mobile sidebar overlay backdrop */
    .sidebar-backdrop {
      display: none;
    }

    /* === Responsive / Mobile === */
    @media (max-width: 768px) {
      .admin-sidebar {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        width: var(--saas-sidebar-width) !important;
        transform: translateX(-100%);
        transition: transform 0.3s ease;
        z-index: 200;
        box-shadow: none;
      }
      .admin-sidebar[data-mobile-open="true"] {
        transform: translateX(0);
        box-shadow: 4px 0 20px rgba(0,0,0,0.15);
      }
      .sidebar-backdrop {
        display: none;
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.4);
        z-index: 199;
      }
      .sidebar-backdrop.visible {
        display: block;
        animation: backdropIn 0.2s ease;
      }
      .header-search {
        display: none;
      }
      .header-user-name {
        display: none;
      }
      .admin-content {
        padding: 16px;
      }
      .top-header {
        padding: 0 16px;
      }
    }
  `}</style>
);

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // === Sidebar state ===
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // === Realtime notification state ===
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [orderRefreshTrigger, setOrderRefreshTrigger] = useState(0);
  const [orderModal, setOrderModal] = useState(null);
  const alarmIntervalRef = useRef(null);
  const alarmCtxRef = useRef(null);
  const alarmTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // === Auto-advance engine (runs as long as any admin page is open) ===
  const [autoAdvanceTrigger, setAutoAdvanceTrigger] = useState(0);
  const autoAdvanceOrdersRef = useRef([]);
  const autoAcceptRef = useRef(false);

  // Load auto_accept setting
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await supabase
          .from('restaurant_settings')
          .select('value')
          .eq('key', 'auto_accept')
          .single();
        if (data) autoAcceptRef.current = data.value === true;
      } catch (e) { /* ignore */ }
    };
    load();
    // Re-check setting every 30s in case toggled from InstantOrders page
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);

  // Fetch single-mode orders for auto-advance
  const fetchAutoAdvanceOrders = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('orders')
        .select('id, status, created_at, preparing_at, ready_at, booking_mode, delivery_info, contact_info')
        .eq('booking_mode', 'single')
        .in('status', ['pending', 'accepted', 'preparing', 'ready'])
        .order('created_at', { ascending: false });
      autoAdvanceOrdersRef.current = data || [];
    } catch (e) {
      // silently ignore
    }
  }, []);

  // Fetch on mount + whenever new orders arrive
  useEffect(() => { fetchAutoAdvanceOrders(); }, [fetchAutoAdvanceOrders, orderRefreshTrigger]);

  // Core auto-advance interval — checks every 5 seconds
  useEffect(() => {
    const inFlight = new Set();

    const advanceOrder = async (orderId, nextStatus) => {
      if (inFlight.has(orderId)) return;
      inFlight.add(orderId);
      try {
        await updateOrderStatus(orderId, nextStatus);
        await fetchAutoAdvanceOrders();
        setAutoAdvanceTrigger(prev => prev + 1);
      } catch (e) {
      } finally {
        inFlight.delete(orderId);
      }
    };

    const check = () => {
      const orders = autoAdvanceOrdersRef.current;
      const checkNow = new Date();

      orders.forEach(o => {
        // 1. Auto-accept instant pending → preparing (after 60s)
        if (o.status === 'pending' && autoAcceptRef.current) {
          const isScheduled = isScheduledOrder(o);
          const farScheduled = isScheduled && getMinutesUntilScheduled(o, checkNow) > ADVANCE_MINUTES;
          if (!isScheduled || !farScheduled) {
            const elapsed = (checkNow - new Date(o.created_at)) / 1000;
            if (elapsed >= AUTO_ACCEPT_SECONDS) {
              advanceOrder(o.id, 'preparing');
              return;
            }
          }
        }

        // 2. Auto-accept scheduled pending → accepted (after 60s, >20min away)
        if (o.status === 'pending' && isScheduledOrder(o)) {
          if (getMinutesUntilScheduled(o, checkNow) > ADVANCE_MINUTES) {
            const elapsed = (checkNow - new Date(o.created_at)) / 1000;
            if (elapsed >= AUTO_ACCEPT_SECONDS) {
              advanceOrder(o.id, 'accepted');
              return;
            }
          }
        }

        // 3. Auto-prepare: accepted scheduled → preparing (within 20min window)
        if (o.status === 'accepted' && isScheduledOrder(o)) {
          if (getMinutesUntilScheduled(o, checkNow) <= ADVANCE_MINUTES) {
            advanceOrder(o.id, 'preparing');
            return;
          }
        }

        // 4. Auto-ready: preparing → ready (after 20min)
        if (o.status === 'preparing' && o.preparing_at) {
          const elapsed = (checkNow - new Date(o.preparing_at)) / 60000;
          if (elapsed >= AUTO_READY_MINUTES) {
            advanceOrder(o.id, 'ready');
            return;
          }
        }

        // 5. Auto-complete: ready pickup → completed (after 5min)
        if (o.status === 'ready' && o.ready_at) {
          const isDelivery = o.delivery_info?.order_type === 'delivery';
          if (!isDelivery) {
            const elapsed = (checkNow - new Date(o.ready_at)) / 60000;
            if (elapsed >= AUTO_COMPLETE_MINUTES) {
              advanceOrder(o.id, 'completed');
            }
          }
        }
      });
    };

    check();
    const timer = setInterval(check, 5000);
    return () => clearInterval(timer);
  }, [fetchAutoAdvanceOrders]);

  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Web Audio API alarm
  const startAlarmSound = useCallback(() => {
    stopAlarmSound();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      alarmCtxRef.current = ctx;
      const vol = 0.6;

      const playNote = (freq, start, dur) => {
        if (ctx.state === 'closed') return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(vol, start);
        gain.gain.exponentialRampToValueAtTime(vol * 0.6, start + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, start + dur);
        osc.start(start);
        osc.stop(start + dur);
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = freq * 3;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(vol * 0.15, start);
        gain2.gain.exponentialRampToValueAtTime(0.01, start + dur * 0.5);
        osc2.start(start);
        osc2.stop(start + dur * 0.5);
      };

      const playMelody = () => {
        if (ctx.state === 'closed') return;
        const t = ctx.currentTime;
        const melody = [
          { freq: 659,  time: 0,    dur: 0.18 },
          { freq: 784,  time: 0.17, dur: 0.18 },
          { freq: 880,  time: 0.34, dur: 0.18 },
          { freq: 1047, time: 0.51, dur: 0.30 },
          { freq: 988,  time: 0.85, dur: 0.18 },
          { freq: 1047, time: 1.02, dur: 0.18 },
          { freq: 1319, time: 1.19, dur: 0.45 },
        ];
        melody.forEach(({ freq, time, dur }) => {
          playNote(freq, t + time, dur);
        });
      };

      playMelody();
      alarmIntervalRef.current = setInterval(playMelody, 2800);
      // Auto-stop after 2 minutes
      alarmTimeoutRef.current = setTimeout(() => stopAlarmSound(), 120000);
    } catch (e) {
      // silently ignore
    }
  }, []);

  const stopAlarmSound = useCallback(() => {
    if (alarmTimeoutRef.current) {
      clearTimeout(alarmTimeoutRef.current);
      alarmTimeoutRef.current = null;
    }
    if (alarmIntervalRef.current) {
      clearInterval(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }
    if (alarmCtxRef.current) {
      alarmCtxRef.current.close().catch(() => {});
      alarmCtxRef.current = null;
    }
  }, []);

  const sendBrowserNotification = useCallback((orderNumber, amount) => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      new Notification('新訂單來了！', {
        body: `#${orderNumber} — $${amount?.toFixed(2) || '0.00'}`,
        icon: '/favicon.ico',
        tag: 'new-order',
        requireInteraction: true,
      });
    }
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const showOrderModal = useCallback((orderId, orderNumber, amount, customerName, bookingMode) => {
    setOrderModal({ orderId, orderNumber, amount, customerName, bookingMode });
  }, []);

  const dismissModal = useCallback(() => {
    stopAlarmSound();
    setOrderModal(null);
  }, [stopAlarmSound]);

  const handleBellClick = useCallback(() => {
    setNewOrderCount(0);
    navigate('/admin/instant-orders');
  }, [navigate]);

  // Supabase Realtime
  useEffect(() => {
    const channel = supabase
      .channel('admin-new-orders')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => {
          const { id, order_number, total_amount, contact_info, booking_mode } = payload.new;
          const customerName = contact_info?.name || '未知客戶';
          setNewOrderCount(prev => prev + 1);
          setOrderRefreshTrigger(prev => prev + 1);
          showOrderModal(id, order_number, total_amount, customerName, booking_mode);
          startAlarmSound();
          sendBrowserNotification(order_number, total_amount);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [showOrderModal, startAlarmSound, sendBrowserNotification]);

  const handleLogout = async () => {
    if (window.confirm('確定要登出管理後台嗎？')) {
      await signOut();
      navigate('/');
    }
  };

  const menuItems = [
    { label: '總覽看板', icon: <LayoutDashboard size={20} />, path: '/admin' },
    { label: '即時訂單', icon: <Zap size={20} />, path: '/admin/instant-orders', badge: true },
    { label: '菜單設定', icon: <Utensils size={20} />, path: '/admin/menu' },
    { label: '交易管理', icon: <ShoppingBag size={20} />, path: '/admin/orders' },
    { label: '週餐訂閱', icon: <CalendarRange size={20} />, path: '/admin/weekly-orders' },
    { label: '配送排程', icon: <Truck size={20} />, path: '/admin/delivery-schedule' },
    { label: '路線規劃', icon: <Map size={20} />, path: '/admin/route' },
    { label: '單點配送區域', icon: <MapPin size={20} />, path: '/admin/single-delivery-zone' },
    { label: '會員列表', icon: <Users size={20} />, path: '/admin/users' },
    { label: '打印機管理', icon: <Printer size={20} />, path: '/admin/printer-setup' },
    { label: '優惠設定', icon: <Settings size={20} />, path: '/admin/settings' },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const currentUser = { name: 'Admin', role: '店長' };

  // Close mobile menu on navigation
  const handleMenuItemClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AdminLayoutStyles />
      <div className="admin-container">
        {/* Mobile backdrop */}
        <div
          className={`sidebar-backdrop ${mobileMenuOpen ? 'visible' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        <aside
          className="admin-sidebar"
          data-collapsed={sidebarCollapsed}
          data-mobile-open={mobileMenuOpen}
        >
          {/* Gradient profile section */}
          <div className="sidebar-profile-section">
            <div className="sidebar-avatar">
              <img src={logoImg} alt="Peaches" />
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{currentUser.name}</div>
              <div className="sidebar-user-role">{currentUser.role}</div>
            </div>
          </div>

          {/* Section label */}
          <div className="sidebar-section-label">MENU</div>

          <ul className="sidebar-menu">
            {menuItems.map((item) => (
              <li
                key={item.path}
                className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleMenuItemClick(item.path)}
                title={item.label}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.badge && newOrderCount > 0 && (
                  <span className="menu-item-badge">
                    {newOrderCount > 99 ? '99+' : newOrderCount}
                  </span>
                )}
              </li>
            ))}
          </ul>

          <div className="sidebar-footer">
            <button onClick={() => navigate('/')} className="back-to-front-btn">
              <ExternalLink size={18} />
              <span>返回前台</span>
            </button>
          </div>
        </aside>

        <div className="main-wrapper">
          <header className="top-header">
            <div className="header-left">
              <button
                className="hamburger-btn"
                onClick={() => {
                  if (window.innerWidth <= 768) {
                    setMobileMenuOpen(!mobileMenuOpen);
                  } else {
                    setSidebarCollapsed(!sidebarCollapsed);
                  }
                }}
                title="Toggle sidebar"
              >
                <MenuIcon size={20} />
              </button>
              <div className="header-search">
                <Search size={18} />
                <input type="text" placeholder="搜尋訂單、會員或菜單..." />
              </div>
            </div>

            <div className="header-actions">
              <button className="icon-btn" onClick={toggleFullscreen} title="Toggle fullscreen">
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
              <button className="icon-btn" onClick={handleBellClick} title="Notifications">
                <Bell size={20} />
                {newOrderCount > 0 && (
                  <span className="notification-badge-count">
                    {newOrderCount > 99 ? '99+' : newOrderCount}
                  </span>
                )}
              </button>
              <div className="header-user-dropdown" ref={dropdownRef} onClick={() => setUserDropdownOpen(!userDropdownOpen)}>
                <div className="header-user-avatar">
                  <img src={logoImg} alt="Peaches" />
                </div>
                <span className="header-user-name">{currentUser.name}</span>
                <ChevronDown size={16} color="#64748b" />
                {userDropdownOpen && (
                  <div className="header-dropdown-menu">
                    <button
                      className="dropdown-item dropdown-item-danger"
                      onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                    >
                      <LogOut size={16} />
                      登出
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <main className="admin-content">
            <Outlet context={{ orderRefreshTrigger, autoAdvanceTrigger }} />
          </main>
        </div>

        {/* New Order Modal */}
        {orderModal && (
          <div className="order-modal-backdrop" onClick={dismissModal}>
            <div className="order-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-bell-wrapper">
                <Bell size={32} color="white" />
              </div>
              <div className="modal-title">新訂單來了！</div>
              <div className="modal-order-number">#{orderModal.orderNumber}</div>
              <div className="modal-customer">{orderModal.customerName}</div>
              <div className="modal-amount">${orderModal.amount?.toFixed(2) || '0.00'}</div>
              <div className="modal-actions">
                <button className="modal-btn modal-btn-secondary" onClick={dismissModal}>
                  知道了
                </button>
                <button className="modal-btn modal-btn-primary" onClick={() => {
                  const oid = orderModal.orderId;
                  const target = orderModal.bookingMode === 'weekly'
                    ? '/admin/weekly-orders'
                    : '/admin/instant-orders';
                  dismissModal();
                  navigate(target, { state: { scrollToOrderId: oid } });
                }}>
                  查看訂單
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminLayout;
